import * as XLSX from 'xlsx-js-style';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { buildReportWorkbook } from './ExcelReport';
import { beautifyNumber } from './FormatNumber';
import Constants from '../assests/Constants/constants';

dayjs.extend(utc);

const dash = '—';

const localizeReportStatus = (t, status) => {
  if (!status) return t('notReported');
  return t(status) || status;
};

const formatRegisterDate = (value) => {
  if (!value) return '';
  const date = dayjs(value);
  return date.isValid() ? date.format('MM/YYYY') : String(value);
};

const formatWithholdingDate = (value) => {
  if (!value) return '';
  const date = dayjs.utc(value).startOf('day');
  if (!date.isValid()) return String(value);
  return date.format('DD/MM/YYYY');
};

const paymentMethodLabel = (t, value) => {
  const isClient =
    value === 1 ||
    value === '1' ||
    String(value) === Constants.ClientPaymentMethod?.Client ||
    value === Constants.clientPaymentMethodOptionsEnum?.Client;
  return isClient ? t('Client') : t('Accountant');
};

const creditCardLabel = (t, client) => {
  const paysAsClient =
    client.clientPaymentMethod === 1 ||
    client.clientPaymentMethod === '1' ||
    String(client.clientPaymentMethod) === Constants.ClientPaymentMethod?.Client ||
    client.clientPaymentMethod === Constants.clientPaymentMethodOptionsEnum?.Client;
  if (!paysAsClient) return dash;
  return client.hasCreditCard ? t('Yes') : t('No');
};

const reportingTypeLabel = (t, client, field) => {
  const value = client[field];
  if (Constants.TaxExemptTypes.includes(client.caseType)) {
    if (field === 'reportingType') return dash;
    return value ? t(value) || value : '';
  }
  return value ? t(value) || value : '';
};

/**
 * Build headers + rows matching the Main clients DataGrid columns.
 */
export const buildClientsTableExportData = ({
  clients,
  t,
  subAccountants = [],
  includeBalance = true,
  includeWithholdingReporting = true,
}) => {
  const headers = [
    t('clientName'),
    t('businessOwnerName'),
    t('CompanyId'),
    t('Number'),
    t('caseType'),
    t('SubAccountantName'),
    t('MonthlyBiMonthly'),
    t('AdvanceIncomeTaxReportType'),
    ...(includeWithholdingReporting ? [t('WithholdingDeductionReport')] : []),
    t('VATReport'),
    t('AdvanceTaxReport'),
    t('unprocessed'),
    t('processedThisMonth'),
    t('UnfinishedAssignments'),
    t('AccountManagement'),
    t('PaymentMethod'),
    t('CreditCard'),
    t('MasavEnabled'),
    t('WithholdingTaxApprovalValidity'),
    t('RegisterDate'),
  ];

  if (includeBalance) headers.push(t('Balance'));

  const rows = clients.map((client) => {
    const sub = subAccountants.find((a) => a.value === client.subAccountantId);
    const isManageOnly = !!client.isManageOnly;
    const name = `${client.leadingBusiness ? '👑 ' : ''}${client.name || ''}`;

    const row = [
      name,
      client.businessOwnerName || '',
      client.caseId || '',
      client.officeIdentifyNumber ?? '',
      client.caseType || '',
      sub?.label || '',
      reportingTypeLabel(t, client, 'reportingType'),
      reportingTypeLabel(t, client, 'advancedReportingType'),
      ...(includeWithholdingReporting ? [client.withholdingReporting ? t('Yes') : t('No')] : []),
      isManageOnly ? dash : localizeReportStatus(t, client.vatReportStatus || 'notReported'),
      isManageOnly ? dash : localizeReportStatus(t, client.advanceReportStatus || 'notReported'),
      isManageOnly ? dash : (client.totalCounts ?? 0),
      isManageOnly ? dash : (client.processedThisMonth ?? 0),
      client.unfinishedAssignments ?? 0,
      client.accountManagement || '',
      paymentMethodLabel(t, client.clientPaymentMethod),
      creditCardLabel(t, client),
      client.isMasavEnabled ? t('Yes') : t('No'),
      isManageOnly ? dash : formatWithholdingDate(client.withholdingTaxApprovalValidUntil),
      formatRegisterDate(client.registerDate),
    ];

    if (includeBalance) {
      const raw = client.balance === '-' || client.balance == null ? 0 : client.balance;
      row.push(beautifyNumber(raw));
    }

    return row;
  });

  return { headers, rows };
};

export const downloadClientsTableExcel = ({
  clients,
  t,
  subAccountants,
  includeBalance,
  includeWithholdingReporting,
  fileName,
  title,
}) => {
  const { headers, rows } = buildClientsTableExportData({
    clients,
    t,
    subAccountants,
    includeBalance,
    includeWithholdingReporting,
  });

  const wb = buildReportWorkbook({
    sheetName: 'Clients',
    titleLines: [title || t('exportSelectedExcel'), `${t('selectedClientsCount', { count: clients.length })}`],
    header: headers,
    rows,
    colWidths: headers.map((h) => Math.max(12, Math.min(28, String(h).length + 4))),
  });

  XLSX.writeFile(wb, fileName || 'ClientsTable.xlsx');
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const downloadClientsTablePdf = async ({
  clients,
  t,
  subAccountants,
  includeBalance,
  includeWithholdingReporting,
  fileName,
  title,
  direction = 'rtl',
}) => {
  const { headers, rows } = buildClientsTableExportData({
    clients,
    t,
    subAccountants,
    includeBalance,
    includeWithholdingReporting,
  });

  const html = `
    <div dir="${direction}" style="font-family: Arial, sans-serif; color: #1f2937; padding: 8px;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
        <div>
          <div style="font-size:18px;font-weight:700;color:#1e3a8a;">${escapeHtml(title || t('exportSelectedPdf'))}</div>
          <div style="font-size:11px;color:#4b5563;">${escapeHtml(t('selectedClientsCount', { count: clients.length }))}</div>
        </div>
      </div>
      <table style="width:100%;border-collapse:collapse;table-layout:fixed;">
        <thead>
          <tr>
            ${headers.map((h) => `<th style="border:1px solid #cbd5e1;background:#1e3a8a;color:#fff;padding:5px;font-size:8px;text-align:center;">${escapeHtml(h)}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${rows.map((row, idx) => `
            <tr style="background:${idx % 2 === 0 ? '#fff' : '#f8fafc'};">
              ${row.map((cell) => `<td style="border:1px solid #cbd5e1;padding:4px;font-size:7.5px;text-align:center;word-wrap:break-word;">${escapeHtml(cell)}</td>`).join('')}
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  `;

  const element = document.createElement('div');
  element.innerHTML = html;
  element.style.position = 'fixed';
  element.style.left = '-10000px';
  element.style.top = '0';
  element.style.width = '1400px';
  element.style.background = '#fff';
  document.body.appendChild(element);

  try {
    const { default: html2pdf } = await import('html2pdf.js');
    await html2pdf()
      .set({
        margin: [8, 6, 8, 6],
        filename: fileName || 'ClientsTable.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
      })
      .from(element)
      .save();
  } finally {
    element.remove();
  }
};
